import React, { useRef, useState } from "react";
import Card from "../../../../components/card";
import { useGlobalFilter, useSortBy, useTable } from "react-table";
import Permissions from "../../../../hooks/perms";

// Permission Denied Popup Component
const PermissionDeniedPopup = ({ isOpen, message, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white dark:bg-navy-800 shadow-lg rounded-xl p-6">
        <h4 className="mb-4 text-xl font-bold text-center text-navy-700 dark:text-white">
          Access Denied
        </h4>
        <p className="mb-6 text-center text-base text-red-500 dark:text-red-400">
          {message}
        </p>
        <div className="flex justify-center">
          <button
            onClick={onClose}
            className="bg-brand-500 hover:bg-brand-600 active:bg-brand-700 text-white dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:bg-brand-200 py-2 px-4 rounded-xl text-base font-medium transition duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ReportTable = ({
  columnsData,
  tableData,
  reportName,
  switchForms,
  handleFormSwitch,
}) => {
  const tableRef = useRef();
  const columns = React.useMemo(() => columnsData, [columnsData]);
  const data = React.useMemo(() => tableData, [tableData]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter,
    useSortBy
  );

  const tableToExcel = (function () {
    const style = "<style>.green { background-color: lightgrey; }</style>";
    const uri = "data:application/vnd.ms-excel;base64,";
    const template =
      '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>{worksheet}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->' +
      style +
      "</head><body><h1>{reportName}</h1><table>{table}</table></body></html>";
    const base64 = function (s) {
      return window.btoa(unescape(encodeURIComponent(s)));
    };
    const format = function (s, c) {
      return s.replace(/{(\w+)}/g, function (m, p) {
        return c[p];
      });
    };
    return function (table, name) {
      const userPermissions = Permissions.getUserPermissions();
      if (!Permissions.checkPermission(userPermissions, Permissions.REPORTS_DOWNLOAD)) {
        setPopupMessage("You have no sufficient permission to download excel reports.");
        setIsPopupOpen(true);
        return;
      }

      if (!table.nodeType) table = document.getElementById(table);
      const ctx = {
        worksheet: name || "Worksheet",
        reportName: name,
        table: table.innerHTML,
      };
      window.location.href = uri + base64(format(template, ctx));
    };
  })();

  return (
    <Card extra={"w-full h-full p-4"}>
      {/* Permission Denied Popup */}
      <PermissionDeniedPopup
        isOpen={isPopupOpen}
        message={popupMessage}
        onClose={() => setIsPopupOpen(false)}
      />
      {/* Header Row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex flex-col">
          <div className="text-xl font-bold text-navy-700 text-black">
            {reportName}
          </div>
          <div className="flex items-center">
            <button
              className="bg-blue-500 text-white px-3 py-1 rounded mr-2"
              onClick={() => tableToExcel(tableRef.current, reportName)}
            >
              Export to Excel
            </button>
            <select
              className="bg-gray-200 px-3 py-1 rounded"
              style={{ color: "black" }}
              onChange={(e) => handleFormSwitch(e.target.value)}
            >
              <option value="">Select Form</option>
              {Object.entries(switchForms).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* Table Body */}
      <div className="overflow-x-auto">
        <table
          {...getTableProps()}
          ref={tableRef}
          className="w-full border-collapse border border-gray-300"
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    key={column.id}
                    className="border border-gray-300 bg-gray-200 px-2 py-1 text-xs text-left"
                    style={{ color: "black" }}
                  >
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row, index) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  className={index % 2 === 0 ? "bg-white" : "bg-gray-100"}
                  style={{ color: "black" }}
                >
                  {row.cells.map((cell) => (
                    <td
                      className="border border-gray-300 px-2 py-1 text-xs whitespace-normal break-words"
                      {...cell.getCellProps()}
                      key={cell.column.id}
                      style={{ color: "black" }}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default ReportTable;